import React from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import useIsMobile from '@/hooks/use-is-mobile';
import {assetThreadTarget} from '@/api/messages';
import {AssetMessageThread} from '@/api/messages/types';
import {MessagesPanel} from '../messages';
import type {Deliverable} from '../types';
import type {CombinedTotals} from '../postRequirements';
import type {GigShellData} from './shellTypes';
import {buildGigTabs, GigTab} from './gigTabs';
import GigSidebar from './GigSidebar';
import styles from './GigShell.module.scss';

// States where the gig has been accepted by both parties, mirroring Ember's paid-gig.js acceptedStates
export const ACCEPTED_STATES = new Set([
  'awaiting_brand_uploads_feedback',
  'awaiting_influencer_posts',
  'awaiting_influencer_uploads',
  'awaiting_payment',
  'completed',
]);

interface GigShellProps {
  gigId: string;
  activeKind: string;
  shell: GigShellData;
  deliverables: Deliverable[];
  totals: CombinedTotals;
  /**
   * False while `totals` cannot be trusted — the submissions query is still loading
   * or failed — so the sidebar omits the bar rather than reporting "0 / 0".
   */
  showProgress?: boolean;
  onSelectKind: (tab: GigTab) => void;
  onBeforeOpenAssetThread?: (thread: AssetMessageThread) => boolean;
  children: React.ReactNode;
}

function GigShell({
  gigId,
  activeKind,
  shell,
  deliverables,
  totals,
  showProgress = true,
  onSelectKind,
  onBeforeOpenAssetThread,
  children,
}: GigShellProps) {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const messagesOpen = searchParams.has('conversationId');

  const closeMessages = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('conversationId');
    setSearchParams(next, {replace: true});
  };

  const {state} = shell;
  const inAcceptedState = ACCEPTED_STATES.has(state);
  const inProgress = inAcceptedState && state !== 'completed';

  const sections = buildGigTabs({
    gigId,
    gig: shell,
    deliverables,
    inAcceptedState,
    inProgress,
    activeKind,
  });

  const handleToggleMessages = () => {
    if (!shell.conversationId) return;
    const next = new URLSearchParams(searchParams);
    next.set('conversationId', shell.conversationId);
    setSearchParams(next, {replace: true});
  };

  const handleOpenAssetThread = (thread: AssetMessageThread) => {
    if (onBeforeOpenAssetThread && !onBeforeOpenAssetThread(thread)) {
      return false;
    }

    const target = assetThreadTarget(thread);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('conversationId');
    nextParams.set('submissionId', target.submissionId);
    nextParams.set('assetId', target.assetId);
    nextParams.set('versionId', target.versionId);
    navigate(`/gigs/${gigId}?${nextParams.toString()}`);
    return true;
  };

  return (
    <div
      className={isMobile ? `${styles.shell} ${styles.mobile}` : styles.shell}
    >
      <GigSidebar
        gigId={gigId}
        shell={shell}
        sections={sections}
        activeKind={activeKind}
        inAcceptedState={inAcceptedState}
        showProgress={showProgress}
        needsActionCount={totals.needsAction}
        totalApproved={totals.done}
        totalRequired={totals.required}
        onSelectUploads={() =>
          onSelectKind({
            key: 'deliverables',
            label: 'Deliverables',
            to: `/gigs/${gigId}`,
            isReact: true,
          })
        }
        onToggleMessages={handleToggleMessages}
      />
      <main className={styles.content}>{children}</main>
      <MessagesPanel
        open={messagesOpen}
        gigId={gigId}
        campaignId={shell.campaignId}
        conversationId={shell.conversationId}
        supportConversationId={shell.supportConversationId}
        merchantName={shell.merchantName}
        negotiationResolved={shell.gigScope !== 'pending'}
        onOpenAssetThread={handleOpenAssetThread}
        onClose={closeMessages}
      />
    </div>
  );
}

export default GigShell;
